/**
 * ComponentRenderer - Renders individual worksheet component types
 *
 * Used in preview mode to show how components will appear when printed.
 * Each component type has a specific visual representation.
 */

import { cn } from "@/lib/utils";
import type { WorksheetComponent } from "./types";

// ============================================================================
// Component Type Renderers
// ============================================================================

interface ComponentRendererProps {
  component: WorksheetComponent;
  showAnswer?: boolean;
  className?: string;
}

/**
 * Name Field - Student name input line
 */
function NameField({ component, className }: ComponentRendererProps) {
  return (
    <div className={cn("flex items-baseline gap-2", className)}>
      <span className="text-sm font-medium">{component.prompt}</span>
      <div className="flex-1 border-b-2 border-gray-400 min-w-[100px]" />
    </div>
  );
}

/**
 * Text Write - Open text answer field
 */
function TextWrite({ component, className }: ComponentRendererProps) {
  return (
    <div className={cn("space-y-2", className)}>
      <p className="text-sm font-medium">{component.prompt}</p>
      {component.instructions && (
        <p className="text-xs text-muted-foreground italic">
          {component.instructions}
        </p>
      )}
      <div className="border-2 border-gray-300 rounded-md bg-white min-h-[60px] p-2">
        <div className="h-full flex flex-col gap-2">
          <div className="border-b border-gray-200" />
          <div className="border-b border-gray-200" />
          <div className="border-b border-gray-200" />
        </div>
      </div>
    </div>
  );
}

/**
 * Word Problem - Text problem with answer blank
 */
function WordProblem({ component, showAnswer, className }: ComponentRendererProps) {
  return (
    <div className={cn("space-y-2", className)}>
      <p className="text-sm">{component.prompt}</p>
      <div className="flex items-baseline gap-2">
        <span className="text-sm">Answer:</span>
        <div className="w-24 border-b-2 border-gray-400 text-center">
          {showAnswer && component.answerKey && (
            <span className="text-blue-600 text-sm">
              {typeof component.answerKey === "string"
                ? component.answerKey
                : ""}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Number Blank - Single number input blank
 */
function NumberBlank({ component, showAnswer, className }: ComponentRendererProps) {
  return (
    <div className={cn("flex items-baseline gap-2", className)}>
      <span className="text-sm">{component.prompt}</span>
      <div className="w-16 border-b-2 border-gray-400 text-center">
        {showAnswer && component.answerKey && (
          <span className="text-blue-600 text-sm font-medium">
            {typeof component.answerKey === "string" ||
            typeof component.answerKey === "number"
              ? String(component.answerKey)
              : ""}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Number Write - Number writing with extraction
 */
function NumberWrite({ component, showAnswer, className }: ComponentRendererProps) {
  return (
    <div className={cn("space-y-1", className)}>
      <p className="text-sm">{component.prompt}</p>
      <div className="border-2 border-gray-300 rounded-full px-4 py-2 bg-white text-center min-w-[80px] inline-block">
        {showAnswer && component.answerKey && (
          <span className="text-blue-600 font-bold text-lg">
            {typeof component.answerKey === "string" ||
            typeof component.answerKey === "number"
              ? String(component.answerKey)
              : ""}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Circle Options - Multiple choice (circle the answer)
 */
function CircleOptions({ component, showAnswer, className }: ComponentRendererProps) {
  const options = component.options || [];
  const correctAnswer = component.answerKey;

  return (
    <div className={cn("space-y-2", className)}>
      <p className="text-sm font-medium">{component.prompt}</p>
      <div className="flex flex-wrap gap-4">
        {options.map((option, index) => {
          const isCorrect =
            showAnswer &&
            (correctAnswer === option ||
              (Array.isArray(correctAnswer) && correctAnswer.includes(option)));

          return (
            <div
              key={index}
              className={cn(
                "px-4 py-2 border-2 rounded-full text-sm transition-colors",
                isCorrect
                  ? "border-blue-500 bg-blue-50 text-blue-700"
                  : "border-gray-300 bg-white"
              )}
            >
              {option}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Draw Box - Area for student drawing
 */
function DrawBox({ component, className }: ComponentRendererProps) {
  return (
    <div className={cn("space-y-2", className)}>
      <p className="text-sm font-medium">{component.prompt}</p>
      {component.instructions && (
        <p className="text-xs text-muted-foreground italic">
          {component.instructions}
        </p>
      )}
      <div className="border-2 border-gray-300 rounded-md bg-white aspect-video flex items-center justify-center min-h-[100px]">
        <span className="text-xs text-muted-foreground">[Drawing area]</span>
      </div>
    </div>
  );
}

/**
 * Number Sentence - Math equation format (___ + ___ = ___)
 */
function NumberSentence({ component, showAnswer, className }: ComponentRendererProps) {
  // Parse the answer key if it's an object
  let addend1 = "";
  let addend2 = "";
  let sum = "";

  if (showAnswer && component.answerKey && typeof component.answerKey === "object") {
    const key = component.answerKey as { addend1?: number; addend2?: number; sum?: number };
    addend1 = key.addend1?.toString() || "";
    addend2 = key.addend2?.toString() || "";
    sum = key.sum?.toString() || "";
  }

  return (
    <div className={cn("space-y-2", className)}>
      <p className="text-sm font-medium">{component.label}</p>
      <div className="flex items-center gap-2 text-lg">
        <div className="w-12 border-b-2 border-gray-400 text-center">
          {showAnswer && <span className="text-blue-600">{addend1}</span>}
        </div>
        <span>+</span>
        <div className="w-12 border-b-2 border-gray-400 text-center">
          {showAnswer && <span className="text-blue-600">{addend2}</span>}
        </div>
        <span>=</span>
        <div className="w-12 border-b-2 border-gray-400 text-center">
          {showAnswer && <span className="text-blue-600">{sum}</span>}
        </div>
      </div>
    </div>
  );
}

/**
 * Tally Marks - Tally counting area
 */
function TallyMarks({ component, showAnswer, className }: ComponentRendererProps) {
  // Render tally marks if showing answer
  const renderTallies = (count: number) => {
    const groups = Math.floor(count / 5);
    const remainder = count % 5;
    const tallies = [];

    for (let i = 0; i < groups; i++) {
      tallies.push(
        <span key={`group-${i}`} className="inline-block mr-2 font-mono text-lg">
          |||| {/* Crossed by / */}
        </span>
      );
    }

    if (remainder > 0) {
      tallies.push(
        <span key="remainder" className="inline-block font-mono text-lg">
          {"|".repeat(remainder)}
        </span>
      );
    }

    return tallies;
  };

  const answerValue =
    typeof component.answerKey === "number"
      ? component.answerKey
      : parseInt(String(component.answerKey)) || 0;

  return (
    <div className={cn("space-y-2", className)}>
      <p className="text-sm font-medium">{component.prompt}</p>
      <div className="border-2 border-gray-300 rounded-md bg-white p-4 min-h-[80px]">
        {showAnswer && answerValue > 0 ? (
          <div className="text-blue-600">{renderTallies(answerValue)}</div>
        ) : (
          <span className="text-xs text-muted-foreground">[Tally area]</span>
        )}
      </div>
    </div>
  );
}

/**
 * Base Ten Blocks - Place value visual representation
 */
function BaseTenBlocks({ component, showAnswer, className }: ComponentRendererProps) {
  // Parse the answer key if it's an object
  let tens = 0;
  let ones = 0;

  if (showAnswer && component.answerKey && typeof component.answerKey === "object") {
    const key = component.answerKey as { tens?: number; ones?: number };
    tens = key.tens || 0;
    ones = key.ones || 0;
  }

  return (
    <div className={cn("space-y-2", className)}>
      <p className="text-sm font-medium">{component.prompt}</p>
      <div className="border-2 border-gray-300 rounded-md bg-white p-4 min-h-[120px]">
        {showAnswer ? (
          <div className="flex gap-4">
            {/* Tens rods */}
            <div className="flex gap-1">
              {Array.from({ length: tens }).map((_, i) => (
                <div
                  key={`ten-${i}`}
                  className="w-4 h-20 bg-blue-500 rounded-sm"
                  title="10"
                />
              ))}
            </div>
            {/* Ones cubes */}
            <div className="flex flex-wrap gap-1 items-end">
              {Array.from({ length: ones }).map((_, i) => (
                <div
                  key={`one-${i}`}
                  className="w-4 h-4 bg-green-500 rounded-sm"
                  title="1"
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center text-xs text-muted-foreground">
            [Base ten blocks grid]
          </div>
        )}
      </div>
      {showAnswer && (
        <p className="text-xs text-blue-600">
          {tens} tens + {ones} ones = {tens * 10 + ones}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Main Component Renderer
// ============================================================================

export function ComponentRenderer({
  component,
  showAnswer = false,
  className,
}: ComponentRendererProps) {
  const props = { component, showAnswer, className };

  switch (component.type) {
    case "name":
      return <NameField {...props} />;
    case "text_write":
      return <TextWrite {...props} />;
    case "word_problem":
      return <WordProblem {...props} />;
    case "number_blank":
      return <NumberBlank {...props} />;
    case "number_write":
      return <NumberWrite {...props} />;
    case "circle_options":
      return <CircleOptions {...props} />;
    case "draw_box":
      return <DrawBox {...props} />;
    case "number_sentence":
      return <NumberSentence {...props} />;
    case "tally_marks":
      return <TallyMarks {...props} />;
    case "base_ten_blocks":
      return <BaseTenBlocks {...props} />;
    default:
      return (
        <div className={cn("p-4 border border-dashed rounded-md", className)}>
          <p className="text-sm text-muted-foreground">
            Unknown component type: {component.type}
          </p>
        </div>
      );
  }
}
